import { useMemo, useState } from 'react';
import { SearchableSelect } from './searchable-select';
import type { Option } from './searchable-select';
import { useReferralSearch } from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import type { ReferralPartner, ReferralPartnerType } from '../../services/httpServices/referralService';

interface ReferralSearchSelectProps {
  value: string;
  onChange: (partnerId: string, partner?: ReferralPartner) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Label of the currently selected referral (kept visible while searching). */
  selectedLabel?: string;
  onAddNew?: (query: string) => void;
  addNewLabel?: string;
  /** Restrict the feed to a referral kind: DOCTOR | EMPLOYEE | OTHER (or ALL). */
  type?: ReferralPartnerType | 'ALL';
}

/**
 * Searchable dropdown of referral partners (employees and external referrers).
 * Options show the type badge, code, organization/designation and default share
 * so counter staff can disambiguate quickly. Selecting returns the full partner
 * so callers can prefill the referral share %.
 */
export function ReferralSearchSelect({
  value,
  onChange,
  placeholder = 'Search referral by name / mobile…',
  disabled = false,
  selectedLabel,
  onAddNew,
  addNewLabel,
  type = 'ALL',
}: ReferralSearchSelectProps) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 300);

  const { data, isLoading } = useReferralSearch(debounced.trim() || undefined, 25, type);
  const partners = useMemo(() => data?.data ?? [], [data]);

  const options = useMemo<Option[]>(() => {
    const base: Option[] = partners.map((p: ReferralPartner) => ({
      value: p.id,
      label: p.name,
      description: [
        p.type === 'DOCTOR' ? 'Doctor' : p.type === 'EMPLOYEE' ? 'Employee' : 'Other',
        p.code,
        p.organization || p.designation || p.specialization,
        `default ${Number(p.defaultSharePercent) || 0}%`,
      ]
        .filter(Boolean)
        .join(' · '),
      keywords: [p.mobileNumber, p.code, p.organization, p.designation].filter(Boolean) as string[],
    }));

    // Keep the current selection visible even if it is not in the active search
    // results (searching must never blank out the chosen referral).
    if (value && !base.some((o) => o.value === value)) {
      base.unshift({ value, label: selectedLabel || 'Selected referral', description: undefined });
    }
    return base;
  }, [partners, value, selectedLabel]);

  return (
    <SearchableSelect
      value={value}
      onChange={(id) => onChange(id, partners.find((p) => p.id === id))}
      options={options}
      placeholder={placeholder}
      loading={isLoading}
      disabled={disabled}
      onQueryChange={setQuery}
      onAddNew={onAddNew}
      addNewLabel={addNewLabel}
    />
  );
}
